/* eslint-disable react-refresh/only-export-components */

import { lazy } from 'react'
import { useTranslation } from 'react-i18next'
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom'
import { AuthLayout, BlankLayout, DashboardLayout, InnerNavigationLayout } from '@app/layouts'
// Только константа канала, без барреля страницы: тот тянет за собой саму
// страницу и сломал бы её ленивую загрузку.
import { AdminChannel } from '@pages/admin/messages/model/channels'
import { ROLES } from '@shared/config/constants'
import { MemberCardTab, QUERY_PARAMS, ROUTES } from '@shared/config/routes'
import { RoleRoute } from './RoleRoute'
import { RootRedirect } from './RootRedirect'

// ─── Inner navigation wrappers ───────────────────────────────────────────────

function AdminResultsNav() {
  const { t } = useTranslation('admin')
  return (
    <InnerNavigationLayout
      items={[
        { label: t('results.results_page'), value: ROUTES.ADMIN_RESULTS },
        { label: t('results.results-overall-rate'), value: ROUTES.ADMIN_RESULTS_OVERALL },
        { label: t('results.results-countries-rate'), value: ROUTES.ADMIN_RESULTS_COUNTRIES },
      ]}
    />
  )
}

// Both wizard routes render the same component type as route siblings, so
// React would otherwise reuse the instance (and its step/guard state) on a
// new↔edit transition. Key by the :id param to force a remount per identity.
function OlympiadWizardRoute() {
  const { id } = useParams()
  return <OlympiadWizardPage key={id ?? ROUTES.ADMIN_OLYMPIAD_NEW} />
}

// Same reason as the wizard above: filters, sorting and row selection are local
// state that must not survive a switch to a different olympiad's participants.
function OlympiadParticipantsRoute() {
  const { id } = useParams()
  return <OlympiadParticipantsPage key={id} />
}

// Выбор языка и места участия — локальный стейт страницы; при переходе на другую
// олимпиаду он не должен переезжать вместе с размонтированным роутом.
function ParticipantOlympiadDetailRoute() {
  const { id } = useParams()
  return <ParticipantOlympiadDetailPage key={id} />
}

function AdminProfileNav() {
  const { t } = useTranslation('common')
  return (
    <InnerNavigationLayout
      items={[
        { label: t('profile'), value: ROUTES.ADMIN_PROFILE },
        { label: t('profile_settings'), value: ROUTES.ADMIN_SETTINGS },
      ]}
      showTitle
      className="max-w-2xl"
    />
  )
}

function RepProfileNav() {
  const { t } = useTranslation('common')
  return (
    <InnerNavigationLayout
      items={[
        { label: t('profile'), value: ROUTES.REPRESENTATIVE_PROFILE },
        { label: t('profile_settings'), value: ROUTES.REPRESENTATIVE_SETTINGS },
      ]}
      showTitle
      className="max-w-2xl"
    />
  )
}

function ParticipantRateNav() {
  const { t } = useTranslation('participant')
  return (
    <InnerNavigationLayout
      items={[
        { label: t('rate_overall.title'), value: ROUTES.PARTICIPANT_RATE_OVERALL },
        { label: t('rate_olympiad.title'), value: ROUTES.PARTICIPANT_RATE_OLYMPIAD },
      ]}
    />
  )
}

function ParticipantProfileNav() {
  const { t } = useTranslation('common')
  return (
    <InnerNavigationLayout
      items={[
        { label: t('my_way'), value: ROUTES.PARTICIPANT_MY_WAY },
        { label: t('personal_data'), value: ROUTES.PARTICIPANT_PERSONAL },
        { label: t('profile_settings'), value: ROUTES.PARTICIPANT_SETTINGS },
      ]}
      showTitle
      className="max-w-2xl"
    />
  )
}

// ─── Auth pages ──────────────────────────────────────────────────────────────
const LoginPage = lazy(() => import('@pages/auth/login'))
const RegisterPage = lazy(() => import('@pages/auth/register'))
const ActivateAccountPage = lazy(() => import('@pages/auth/activate-account'))
const ForgotPasswordPage = lazy(() => import('@pages/auth/forgot-password'))
const ResetPasswordPage = lazy(() => import('@pages/auth/reset-password'))

// ─── Admin pages ─────────────────────────────────────────────────────────────
const AdminDashboard = lazy(() => import('@pages/admin/dashboard'))
const AdminUsersPage = lazy(() => import('@pages/admin/users'))
const AdminOlympiadsPage = lazy(() => import('@pages/admin/olympiads'))
const OlympiadWizardPage = lazy(() => import('@pages/admin/olympiad-wizard'))
const OlympiadParticipantsPage = lazy(() => import('@pages/admin/olympiad-participants'))
const AdminTasksPage = lazy(() => import('@pages/admin/tasks'))
const AdminResultsPage = lazy(() => import('@pages/admin/results'))
const AdminResultsOverallPage = lazy(() => import('@pages/admin/results-overall-rate'))
const AdminResultsCountriesPage = lazy(() => import('@pages/admin/results-countries-rate'))
const AdminMessagesPage = lazy(() => import('@pages/admin/messages'))
const AdminPaymentsPage = lazy(() => import('@pages/admin/payments'))
const AdminCertificatesPage = lazy(() => import('@pages/admin/certificates'))
const AdminProfilePage = lazy(() => import('@pages/admin/profile'))
const AdminSettingsPage = lazy(() => import('@pages/admin/settings'))

// ─── Participant pages ────────────────────────────────────────────────────────
const ParticipantDashboard = lazy(() => import('@pages/participant/dashboard'))
const ParticipantOlympiadsPage = lazy(() => import('@pages/participant/olympiads'))
const ParticipantOlympiadDetailPage = lazy(() => import('@pages/participant/olympiad-detail'))
const ParticipantTasksPage = lazy(() => import('@pages/participant/tasks'))
const TaskDetailPage = lazy(() => import('@pages/participant/task-detail'))
const ParticipantResultsPage = lazy(() => import('@pages/participant/results'))
const MyWayPage = lazy(() => import('@pages/participant/my-way'))
const RateOlympiadPage = lazy(() => import('@pages/participant/rate-olympiad'))
const RateOverallPage = lazy(() => import('@pages/participant/rate-overall'))
const ParticipantPaymentsPage = lazy(() => import('@pages/participant/payments'))
const ParticipantCertificatesPage = lazy(() => import('@pages/participant/certificates'))
const ParticipantInstructionsPage = lazy(() => import('@pages/participant/instructions'))
const ParticipantFaqPage = lazy(() => import('@pages/participant/faq'))
const ParticipantMessagesPage = lazy(() => import('@pages/participant/messages'))
const PersonalPage = lazy(() => import('@pages/participant/personal'))
const ParticipantSettingsPage = lazy(() => import('@pages/participant/settings'))

// ─── Representative pages ─────────────────────────────────────────────────────
const RepDashboard = lazy(() => import('@pages/representative/dashboard'))
const MembersPage = lazy(() => import('@pages/representative/members'))
const MemberCardPage = lazy(() => import('@pages/representative/member-card'))
const LegacyMemberRedirect = lazy(() =>
  import('@pages/representative/member-card').then((m) => ({ default: m.LegacyMemberRedirect }))
)
const RepChatPage = lazy(() => import('@pages/representative/chat'))
const RepResultsPage = lazy(() => import('@pages/representative/results'))
const RepResultsRatePage = lazy(() => import('@pages/representative/results-rate'))
const RepProfilePage = lazy(() => import('@pages/representative/profile'))
const RepProfileSettingsPage = lazy(() => import('@pages/representative/profile-settings'))

export const router = createBrowserRouter([
  // ── Корень: логин, домашний экран роли или олимпиада, с которой участник
  //    ушёл в прокторинг (PROCTOR возвращает именно сюда) ──────────────────
  { index: true, element: <RootRedirect /> },

  // ── Public auth pages ────────────────────────────────────────────────────
  {
    element: <AuthLayout />,
    children: [
      { path: ROUTES.LOGIN, element: <LoginPage /> },
      { path: ROUTES.REGISTER, element: <RegisterPage /> },
      { path: ROUTES.ACTIVATE_ACCOUNT, element: <ActivateAccountPage /> },
      { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
      { path: ROUTES.RESET_PASSWORD, element: <ResetPasswordPage /> },
      {
        path: ROUTES.RESET_PASSWORD_BASE,
        element: <Navigate to={ROUTES.FORGOT_PASSWORD} replace />,
      },
    ],
  },

  // ── Admin (role-protected) ───────────────────────────────────────────────
  {
    element: (
      <RoleRoute role={ROLES.ADMIN}>
        <DashboardLayout />
      </RoleRoute>
    ),
    children: [
      { path: ROUTES.ADMIN, element: <AdminDashboard /> },
      { path: ROUTES.ADMIN_USERS, element: <AdminUsersPage /> },
      { path: ROUTES.ADMIN_OLYMPIADS, element: <AdminOlympiadsPage /> },
      { path: ROUTES.ADMIN_OLYMPIAD_NEW, element: <OlympiadWizardRoute /> },
      { path: ROUTES.ADMIN_OLYMPIAD_EDIT, element: <OlympiadWizardRoute /> },
      { path: ROUTES.ADMIN_OLYMPIAD_PARTICIPANTS, element: <OlympiadParticipantsRoute /> },
      { path: ROUTES.ADMIN_TASKS, element: <AdminTasksPage /> },
      {
        element: <AdminResultsNav />,
        children: [
          { path: ROUTES.ADMIN_RESULTS, element: <AdminResultsPage /> },
          { path: ROUTES.ADMIN_RESULTS_OVERALL, element: <AdminResultsOverallPage /> },
          { path: ROUTES.ADMIN_RESULTS_COUNTRIES, element: <AdminResultsCountriesPage /> },
        ],
      },
      { path: ROUTES.ADMIN_CHAT, element: <AdminMessagesPage /> },
      // Прежние адреса раздела: список представителей и инбокс поддержки были
      // отдельными страницами до объединения 2026-08-22. Закладки и ссылки из
      // писем не должны упираться в пустоту — и обязаны открывать тот канал,
      // за которым шли, а не общий раздел.
      {
        path: ROUTES.ADMIN_CHAT_USERS,
        element: (
          <Navigate
            to={`${ROUTES.ADMIN_CHAT}?${QUERY_PARAMS.channel}=${AdminChannel.representatives}`}
            replace
          />
        ),
      },
      {
        path: ROUTES.ADMIN_SUPPORT,
        element: (
          <Navigate
            to={`${ROUTES.ADMIN_CHAT}?${QUERY_PARAMS.channel}=${AdminChannel.support}`}
            replace
          />
        ),
      },
      { path: ROUTES.ADMIN_PAYMENTS, element: <AdminPaymentsPage /> },
      { path: ROUTES.ADMIN_CERTIFICATES, element: <AdminCertificatesPage /> },
      {
        element: <AdminProfileNav />,
        children: [
          { path: ROUTES.ADMIN_PROFILE, element: <AdminProfilePage /> },
          { path: ROUTES.ADMIN_SETTINGS, element: <AdminSettingsPage /> },
        ],
      },
    ],
  },

  // ── Participant (role-protected) ─────────────────────────────────────────
  {
    element: (
      <RoleRoute role={ROLES.PARTICIPANT}>
        <DashboardLayout />
      </RoleRoute>
    ),
    children: [
      { path: ROUTES.PARTICIPANT_DASHBOARD, element: <ParticipantDashboard /> },
      { path: ROUTES.PARTICIPANT_OLYMPIADS, element: <ParticipantOlympiadsPage /> },
      { path: ROUTES.PARTICIPANT_OLYMPIAD_DETAIL, element: <ParticipantOlympiadDetailRoute /> },
      { path: ROUTES.PARTICIPANT_TASKS, element: <ParticipantTasksPage /> },
      { path: ROUTES.PARTICIPANT_TASK_DETAIL, element: <TaskDetailPage /> },
      { path: ROUTES.PARTICIPANT_RESULTS, element: <ParticipantResultsPage /> },
      {
        element: <ParticipantRateNav />,
        children: [
          { path: ROUTES.PARTICIPANT_RATE_OVERALL, element: <RateOverallPage /> },
          { path: ROUTES.PARTICIPANT_RATE_OLYMPIAD, element: <RateOlympiadPage /> },
        ],
      },
      { path: ROUTES.PARTICIPANT_PAYMENTS, element: <ParticipantPaymentsPage /> },
      { path: ROUTES.PARTICIPANT_CERTIFICATES, element: <ParticipantCertificatesPage /> },
      { path: ROUTES.PARTICIPANT_INSTRUCTIONS, element: <ParticipantInstructionsPage /> },
      { path: ROUTES.PARTICIPANT_FAQ, element: <ParticipantFaqPage /> },
      { path: ROUTES.PARTICIPANT_MESSAGES, element: <ParticipantMessagesPage /> },
      {
        element: <ParticipantProfileNav />,
        children: [
          { path: ROUTES.PARTICIPANT_MY_WAY, element: <MyWayPage /> },
          { path: ROUTES.PARTICIPANT_PERSONAL, element: <PersonalPage /> },
          { path: ROUTES.PARTICIPANT_SETTINGS, element: <ParticipantSettingsPage /> },
        ],
      },
    ],
  },

  // ── Representative (role-protected) ─────────────────────────────────────
  {
    element: (
      <RoleRoute role={ROLES.REPRESENTATIVE}>
        <DashboardLayout />
      </RoleRoute>
    ),
    children: [
      { path: ROUTES.REPRESENTATIVE, element: <RepDashboard /> },
      { path: ROUTES.REPRESENTATIVE_MEMBERS, element: <MembersPage /> },
      { path: ROUTES.REPRESENTATIVE_MEMBER, element: <MemberCardPage /> },
      // Прежние адреса трёх страниц участника: теперь это вкладки одной карточки.
      {
        path: ROUTES.REPRESENTATIVE_MEMBERS_PROGRESS,
        element: <LegacyMemberRedirect tab={MemberCardTab.progress} />,
      },
      {
        path: ROUTES.REPRESENTATIVE_MEMBERS_PAYMENTS,
        element: <LegacyMemberRedirect tab={MemberCardTab.statuses} />,
      },
      {
        path: ROUTES.REPRESENTATIVE_MEMBERS_PERSONAL,
        element: <LegacyMemberRedirect tab={MemberCardTab.profile} />,
      },
      { path: ROUTES.REPRESENTATIVE_CHAT, element: <RepChatPage /> },
      { path: ROUTES.REPRESENTATIVE_RESULTS, element: <RepResultsPage /> },
      { path: ROUTES.REPRESENTATIVE_RESULTS_RATE, element: <RepResultsRatePage /> },
      {
        element: <RepProfileNav />,
        children: [
          { path: ROUTES.REPRESENTATIVE_PROFILE, element: <RepProfilePage /> },
          { path: ROUTES.REPRESENTATIVE_SETTINGS, element: <RepProfileSettingsPage /> },
        ],
      },
    ],
  },

  // ── Blank layout (catch-all for future pages) ───────────────────────────
  {
    element: <BlankLayout />,
    children: [{ path: '*', element: <Navigate to={ROUTES.LOGIN} replace /> }],
  },
])
